/**
 * Job Hunter — small building blocks shared by every screen: score ring,
 * badges, avatars, toggles, tag inputs, segmented filters and the slide-over.
 */
import { KeyboardEvent, ReactNode, useEffect, useState } from 'react';
import clsx from 'clsx';
import { Ban, BadgeCheck, Bot, Flame, Landmark, Plane, ShieldCheck, ShieldQuestionMark, ShieldX, Skull, Snowflake, Thermometer, X } from 'lucide-react';
import type { JhHeat, JhOutcome, JhSignal, JhStatus } from '@/services/job-hunter.service';
import { SearchableSelect } from '@/components/SearchableSelect';
import { HEAT_META, OUTCOME_META, SIGNAL_META, STATUS_META, TONE_CLASS, Tone, agentHex, agentSoft, countryName, flag, hueOf, initials, scoreTone } from '@/lib/job-hunter/format';

export function ScoreRing({ score, size = 52, stroke = 5, label = true }: { score?: number | null; size?: number; stroke?: number; label?: boolean }) {
  const s = Math.max(0, Math.min(100, score ?? 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const { color, label: text } = scoreTone(s);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} title={score == null ? 'Not scored' : `${s} — ${text}`}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-surface-alt)" strokeWidth={stroke} />
        {score != null && (
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={c * (1 - s / 100)} style={{ transition: 'stroke-dashoffset .6s ease' }} />
        )}
      </svg>
      {label && (
        <span className="absolute inset-0 grid place-items-center font-bold tabular-nums tracking-tight" style={{ fontSize: size * 0.32, color: score == null ? 'var(--color-text-muted)' : color }}>
          {score == null ? '—' : s}
        </span>
      )}
    </div>
  );
}

export function Pill({ tone = 'neutral', icon, children, title, className }: { tone?: Tone; icon?: ReactNode; children: ReactNode; title?: string; className?: string }) {
  return <span className={clsx('jh-pill', TONE_CLASS[tone], className)} title={title}>{icon}{children}</span>;
}

const SIGNAL_ICON: Record<JhSignal, ReactNode> = {
  explicit: <Plane size={12} />, likely: <ShieldCheck size={12} />, none: <ShieldQuestionMark size={12} />, excluded: <ShieldX size={12} />,
};
export function SignalBadge({ signal, short }: { signal?: JhSignal; short?: boolean }) {
  if (!signal) return null;
  const m = SIGNAL_META[signal];
  return <Pill tone={m.tone} icon={SIGNAL_ICON[signal]} title={m.hint}>{short ? m.short : m.label}</Pill>;
}

export function StatusBadge({ status }: { status: JhStatus }) {
  const m = STATUS_META[status];
  return <Pill tone={m.tone}><span className="h-1.5 w-1.5 rounded-full bg-current" />{m.label}</Pill>;
}

const HEAT_ICON: Record<JhHeat, ReactNode> = { hot: <Flame size={12} />, warm: <Thermometer size={12} />, cold: <Snowflake size={12} />, dead: <Skull size={12} /> };
export function HeatBadge({ heat }: { heat: JhHeat }) {
  const m = HEAT_META[heat];
  return <Pill tone={m.tone} icon={HEAT_ICON[heat]} title={m.hint}>{m.label}</Pill>;
}

export function OutcomeBadge({ outcome, title }: { outcome?: JhOutcome | 'unscreened'; title?: string }) {
  const m = OUTCOME_META[outcome ?? 'unscreened'];
  return <Pill tone={m.tone} title={title}>{m.label}</Pill>;
}

export function AtsBadge({ fromAts, type }: { fromAts: boolean; type?: string }) {
  return fromAts
    ? <Pill tone="success" icon={<BadgeCheck size={12} />} title="Read from the employer's own careers system — only their HR can post there">{type ? type.charAt(0).toUpperCase() + type.slice(1) : 'ATS verified'}</Pill>
    : <Pill tone="neutral" title="Found on a job board">{type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Board'}</Pill>;
}

export function CountryTag({ cc, city, compact }: { cc?: string; city?: string; compact?: boolean }) {
  if (!cc && !city) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary" title={countryName(cc)}>
      {cc && <span className="text-[15px] leading-none">{flag(cc)}</span>}
      {compact ? (cc ?? city) : [city, countryName(cc)].filter(Boolean).join(', ')}
    </span>
  );
}

export function EmployerAvatar({ name, size = 40 }: { name?: string; size?: number }) {
  const h = hueOf(name);
  return (
    <span className="jh-avatar" style={{ width: size, height: size, fontSize: size * 0.36, background: `linear-gradient(135deg, hsl(${h} 55% 52%), hsl(${(h + 40) % 360} 60% 38%))` }}>
      {initials(name)}
    </span>
  );
}

/** A person: their photo when there is one, else initials on a soft brand tile. Round, unlike employer logos. */
export function CandidateAvatar({ name, photoUrl, size = 40, className }: { name?: string; photoUrl?: string | null; size?: number; className?: string }) {
  if (photoUrl) return <img src={photoUrl} alt={name ?? ''} referrerPolicy="no-referrer" className={clsx('shrink-0 rounded-full object-cover ring-2 ring-surface', className)} style={{ width: size, height: size }} />;
  return (
    <span className={clsx('grid shrink-0 place-items-center rounded-full bg-gold-gradient font-bold tracking-tight text-on-primary ring-2 ring-surface', className)} style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials(name)}
    </span>
  );
}

export interface JhOption<T extends string | number> { value: T; label: string; description?: string; group?: string; icon?: ReactNode; disabled?: boolean; keywords?: string }

/**
 * The CRM's searchable dropdown (same as /templates/new) with Job Hunter's
 * sizes. Search appears once a list is long enough to need it.
 */
export function JhSelect<T extends string | number>({ value, onChange, options, placeholder, size = 'md', className, searchable, numbered = false, ariaLabel, disabled, searchPlaceholder }: {
  value: T | undefined | null;
  onChange: (v: T) => void;
  options: Array<JhOption<T>>;
  placeholder?: string;
  size?: 'sm' | 'md';
  className?: string;
  searchable?: boolean;
  numbered?: boolean;
  ariaLabel?: string;
  disabled?: boolean;
  searchPlaceholder?: string;
}) {
  const byKey = new Map(options.map((o) => [String(o.value), o.value]));
  return (
    <div className={clsx('min-w-0', className)}>
      <SearchableSelect
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(v) => { if (byKey.has(v)) onChange(byKey.get(v) as T); }}
        options={options.map((o) => ({ value: String(o.value), label: o.label, description: o.description, group: o.group, icon: o.icon, disabled: o.disabled, keywords: o.keywords }))}
        placeholder={placeholder}
        searchable={searchable ?? options.length > 8}
        searchPlaceholder={searchPlaceholder}
        numbered={numbered}
        disabled={disabled}
        aria-label={ariaLabel}
        className={size === 'sm' ? '!h-9 !rounded-[10px] !px-3 !text-[12.5px]' : '!rounded-[10px]'}
      />
    </div>
  );
}

export function Toggle({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label?: string }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} className="jh-toggle" onClick={() => onChange(!checked)} />;
}

export function ToggleRow({ title, hint, checked, onChange, disabled }: { title: string; hint?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-border bg-surface px-3.5 py-3">
      <div className="min-w-0">
        <div className="text-[13px] font-semibold">{title}</div>
        {hint && <div className="jh-field-hint mt-0.5">{hint}</div>}
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} label={title} />
    </div>
  );
}

export function Field({ label, hint, children, className }: { label: string; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={clsx('block min-w-0', className)}>
      <span className="jh-field-label">{label}</span>
      {children}
      {hint && <span className="jh-field-hint block">{hint}</span>}
    </label>
  );
}

/** Chips with free typing — Enter or comma adds, Backspace on empty removes the last. */
export function TagInput({ value, onChange, placeholder, transform, max = 60 }: { value: string[]; onChange: (v: string[]) => void; placeholder?: string; transform?: (s: string) => string; max?: number }) {
  const [draft, setDraft] = useState('');
  const add = (raw: string) => {
    const parts = raw.split(/[,\n]/).map((s) => (transform ? transform(s.trim()) : s.trim())).filter(Boolean);
    if (!parts.length) return;
    const next = [...value];
    for (const p of parts) if (!next.includes(p) && next.length < max) next.push(p);
    onChange(next);
    setDraft('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(draft); }
    if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
  };
  return (
    <div className="jh-tags">
      {value.map((t) => (
        <span key={t} className="jh-tag">{t}<button type="button" aria-label={`Remove ${t}`} onClick={() => onChange(value.filter((x) => x !== t))}><X size={11} /></button></span>
      ))}
      <input value={draft} placeholder={value.length ? '' : placeholder} onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={() => add(draft)} onPaste={(e) => { const t = e.clipboardData.getData('text'); if (/[,\n]/.test(t)) { e.preventDefault(); add(t); } }} />
    </div>
  );
}

export function Segmented<T extends string>({ options, value, onChange }: { options: Array<{ value: T; label: string; n?: number; icon?: ReactNode }>; value: T; onChange: (v: T) => void }) {
  return (
    <div className="jh-seg" role="group">
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.icon}{o.label}{o.n !== undefined && <span className="jh-seg-n">{o.n}</span>}
        </button>
      ))}
    </div>
  );
}

export function Meter({ value, max, tone = 'primary' }: { value: number; max: number; tone?: Tone }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  const color = pct > 90 ? 'var(--color-danger)' : pct > 70 ? 'var(--color-warning)' : tone === 'success' ? 'var(--color-success)' : 'var(--color-primary)';
  return <div className="jh-meter"><span style={{ width: `${pct}%`, background: color }} /></div>;
}

export function Drawer({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: globalThis.KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return <>
    <div className="jh-drawer-backdrop" onClick={onClose} />
    <aside className="jh-drawer" role="dialog" aria-modal="true">{children}</aside>
  </>;
}

export function Notice({ tone = 'info', title, children, action }: { tone?: Tone; title: string; children?: ReactNode; action?: ReactNode }) {
  const border = tone === 'danger' ? 'border-danger/40' : tone === 'warning' ? 'border-warning/40' : tone === 'success' ? 'border-success/40' : 'border-border';
  return (
    <div className={clsx('mb-4 flex flex-wrap items-start justify-between gap-3 rounded-xl border bg-surface px-4 py-3', border)} role={tone === 'danger' ? 'alert' : 'status'}>
      <div className="min-w-0 flex-1">
        <div className={clsx('text-[13px] font-semibold', tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : tone === 'success' ? 'text-success' : 'text-text-primary')}>{title}</div>
        {children && <div className="mt-0.5 text-[12.5px] leading-relaxed text-text-secondary">{children}</div>}
      </div>
      {action}
    </div>
  );
}

export function BlockedTag() {
  return <Pill tone="danger" icon={<Ban size={12} />}>Closed</Pill>;
}

/** Wraps sponsorship / visa words in <mark> for the description view. */
export function highlight(text: string, words: string[]): ReactNode[] {
  const list = words.filter((w) => w.trim().length > 2).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!list.length) return [text];
  const re = new RegExp(`(${list.join('|')})`, 'gi');
  return text.split(re).map((part, i) => (i % 2 === 1 ? <mark key={i}>{part}</mark> : part));
}

/** The agent that found a match — its colour and name. */
export function AgentChip({ name, color, size = 'sm' }: { name?: string; color?: string; size?: 'sm' | 'xs' }) {
  if (!name) return null;
  return (
    <span className={clsx('jh-chip !font-semibold', size === 'xs' && '!px-2 !py-0.5 !text-[10.5px]')} style={{ background: agentSoft(color), color: agentHex(color) }} title={`Found by the “${name}” agent`}>
      <Bot size={size === 'xs' ? 10 : 11} />{name}
    </span>
  );
}

/** Country flags, the first few and "+N". */
export function Flags({ codes, max = 6 }: { codes: string[]; max?: number }) {
  if (!codes.length) return <span className="text-xs text-text-muted">Any country</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-[15px] leading-none" title={codes.map(countryName).join(', ')}>
      {codes.slice(0, max).map((c) => <span key={c}>{flag(c)}</span>)}
      {codes.length > max && <span className="jh-chip !text-[10.5px]">+{codes.length - max}</span>}
    </span>
  );
}

/** Visa route + official register line for a match. */
export function VisaLine({ route, register }: { route?: string; register?: string }) {
  if (!route && !register) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-text-secondary">
      {route && <span className="inline-flex items-center gap-1"><Plane size={11} className="text-primary" />{route}</span>}
      {register && <span className="inline-flex min-w-0 items-center gap-1" title={register}><Landmark size={11} className="shrink-0 text-success" /><span className="truncate">{register}</span></span>}
    </div>
  );
}
